import { EventEmitter, Injectable } from '@angular/core';
import { RepositoryService } from '../linq-utils/repository.service';
import { Notification, ONBOARDING_STEPS, PROFILE_KEY, Preferences, PreferencesChange, SupportTicket, USER_ROLE, UTMCampaign, User, changedDrawingPreferences, convertOldPreferences, normalisedPreferences } from '../common/user';
import { CookieService } from 'ngx-cookie-service';
import { Organisation } from '../common/organisation';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { signedInUrl } from './onboarding';

const COMPONENT = "User service"
export const TOKEN_KEY = 'token'

export const searchUsersUrl = (searchName:string, page:number, pageSize:number) => {
    return `api/users?name=${encodeURIComponent(searchName || '')}&page=${page}&limit=${pageSize}`
}

export const userUrl = (userKey:string) => {
    return 'api/users/' + userKey
}

export const supportTicketUrl = () => {
    return 'api/support/tickets'
}

@Injectable({
  providedIn: 'root'
})
export class UserService {
    private userNotifications:Notification[] = []
    // who the authentication provider is
    public provider:string
    public utm:UTMCampaign      // comes from caller when registering so send with login/registration

    public organisationName:string      // used to hold the org name if a social media user registers with one
    public modelKey:string              // holds model key if a guest user and then register using social media

    notificationsEvent = new EventEmitter<string>()
    userChangedEvent = new EventEmitter<string>()
    // the signed-in user's drawing settings changed: the canvases showing models apply them without a refresh
    preferencesChangedEvent = new EventEmitter<PreferencesChange>()

    constructor(private repoService: RepositoryService, private cookieService: CookieService, private router:Router,
        private snackBar:MatSnackBar) { }

    get notifications() {
        return this.userNotifications
    }

    set notifications(n:Notification[]) {
        this.userNotifications = n
        this.notificationsEvent.emit('')
    }

    ngOnDestroy(): void {
        localStorage.removeItem(PROFILE_KEY);
        this.cookieService.delete(TOKEN_KEY);
    }

    public async socialSignIn(token:string) {
        // any earlier session is ended first
        await this.logout()

        try {
            // the session cookie the server sets is httpOnly, so the provider's token goes in the request instead
            const res = await this.repoService.callSingleAPI(this.repoService.create('api/public/signin', {idToken:token, utm:this.utm, organisation:this.organisationName, modelKey:this.modelKey}), COMPONENT, "Sign in social user")
            this.setUser(res as User)
        }catch(err) {
            this.snackBar.open("Provider login failed. You can try again or use a different identity.", "close");
        }
    }

    public async signInGuest() {
        return await this.signIn({email:"guest@linq.it", password:"guest"}, true)
    }

    // showError false leaves showing a problem to the caller, which gets the exception
    public async signIn(credentials:any, stay:boolean=false, showError:boolean=true){
        const res = await this.repoService.callSingleAPI(this.repoService.create('api/public/signin', credentials), COMPONENT, "Sign in user", undefined, undefined, undefined, undefined, showError)
        const u = res as User
        this.setUser(u, stay)
        return u
    }

    public async setUser(user:User, stay:boolean=false){
        localStorage.setItem(PROFILE_KEY, JSON.stringify(user));
        this.userChangedEvent.emit('')
        if (stay) return

        this.router.navigateByUrl(signedInUrl(user, environment.demoOrganisationKey))
    }

    // signing in stores the profile and signing out clears it. The token cookie can't show this: the server may set
    // it so the page can't read it, and reading a missing cookie gives "" (which the old check took as signed in)
    public isLoggedIn() : boolean{
        return !!this.getUser()
    }

    public isSysAdmin(): boolean {
        return this.getUser()?.role === USER_ROLE.ADMIN
    }

    // the session ends here even if the server can't be told (offline, or the token has already expired)
    public async logout() {
        this.notifications = []
        try {
            await this.repoService.callSingleAPI(this.repoService.getData("api/signout"), COMPONENT, "Signout", undefined, undefined, undefined, undefined, false)
        }catch(err) {
            // logged by the repository; nothing for the user to do
        }finally {
            localStorage.clear()
            this.cookieService.deleteAll();
        }
    }

    public getUser():User {
        const jsonUser = localStorage.getItem(PROFILE_KEY);
        if (!jsonUser) return undefined
        try {
            return JSON.parse(jsonUser)
        }catch(err) {
            // a damaged profile is as good as none: sign in again
            return undefined
        }
    }

    public getUserPreferences() {
        const user = this.getUser()
        if (!user) return undefined
        convertOldPreferences(user)         // Changed edge routing and direction values in v2.0.0. should correct over time but do this just in case.
        return user.preferences
    }

    async searchUsersAsAdmin(searchName:string, page:number, pageSize:number) {
        if (!this.isSysAdmin()) return []

        const SERVICE = "Search users as admin"
        return await this.repoService.callSingleAPI(this.repoService.getData(searchUsersUrl(searchName, page, pageSize)), COMPONENT, SERVICE, this.getUser())
    }

    async getUsersAsAdmin() {
        if (!this.isSysAdmin()) return []

        const SERVICE = "Get users as admin"
        return await this.repoService.callSingleAPI(this.repoService.getData('api/admin/users'), COMPONENT, SERVICE, this.getUser())
    }

    // records a step of getting started, once. It happens alongside something else the user did, so it says nothing,
    // and a failure only means the step may be shown again
    public async saveOnboardingStep(step:ONBOARDING_STEPS) {
        const user = this.getUser()
        if (!user) return user
        if (!user.onboardingSteps || user.onboardingSteps.indexOf(step) < 0) {
            if (!user.onboardingSteps) user.onboardingSteps = []
            user.onboardingSteps.push(step)
            this.saveUser(user, false).catch(() => {})
        }
        return user
    }

    public async saveUser(user:User, showSaved:boolean = true) {
        const SERVICE = "Save user"

        await this.repoService.callSingleAPI(this.repoService.update(userUrl(user._key), user), COMPONENT, SERVICE, this.getUser(), undefined, undefined, user, showSaved)

        const currentUser = this.getUser()
        let before:Preferences|undefined
        if (currentUser?._key === user._key) {
            before = currentUser.preferences
            currentUser.name = user.name
            currentUser.email = user.email
            currentUser.preferences = user.preferences
            currentUser.onboardingSteps = user.onboardingSteps
            localStorage.setItem(PROFILE_KEY, JSON.stringify(currentUser));
        }
        this.userChangedEvent.emit('')
        this.announcePreferences(before, user.preferences)
        if (showSaved) this.snackBar.open("User profile saved", "close", {duration:5000});
    }

    // quietly for a choice remembered along the way, such as a view's layout, rather than one made in the settings
    public async saveUserPreferences(user:User, quiet = false) {
        const SERVICE = "Save user preferences"

        await this.repoService.callSingleAPI(this.repoService.update(userUrl(user._key) + "/preferences", user.preferences), COMPONENT, SERVICE, this.getUser(), undefined, undefined, user)

        const currentUser = this.getUser()
        let before:Preferences|undefined
        if (currentUser?._key === user._key) {
            before = currentUser.preferences
            currentUser.preferences = user.preferences
            localStorage.setItem(PROFILE_KEY, JSON.stringify(currentUser));
        }
        this.userChangedEvent.emit('')
        this.announcePreferences(before, user.preferences)
        if (!quiet) this.snackBar.open("Preferences saved", "close", {duration:5000});
    }

    // only the signed-in user's own settings, and only when something a canvas acts on changed
    private announcePreferences(before:Preferences|undefined, after:Preferences|undefined) {
        const changed = changedDrawingPreferences(before, after)
        if (changed.direction || changed.routing || changed.move) this.preferencesChangedEvent.emit({ before: normalisedPreferences(before), after: normalisedPreferences(after) })
    }

    public async inviteUser(organisation:Organisation, user:User) {
        const SERVICE = "Invite user"

        await this.repoService.callSingleAPI(this.repoService.create(`api/organisations/${organisation._key}/users/invite`, user), COMPONENT, SERVICE, this.getUser(), organisation, undefined, user)
        this.snackBar.open("Invite sent", "close", {duration:5000});
    }

    public async deleteUser(user:User) {
        if (!this.isSysAdmin()) return

        const SERVICE = "Delete user"

        await this.repoService.callSingleAPI(this.repoService.delete(userUrl(user._key)), COMPONENT, SERVICE, this.getUser(), undefined, undefined, user)
        this.snackBar.open("User deleted", "close", {duration:5000});
    }

    public async deleteNotification(notification:Notification) {
        const SERVICE = "Delete notification"

        const user = this.getUser()
        if (!user) return
        await this.repoService.callSingleAPI(this.repoService.delete(userUrl(user._key) + `/notifications/${notification._key}`), COMPONENT, SERVICE, user)
        this.notifications = this.notifications.filter(n => n._key !== notification._key)
        this.snackBar.open("Notification deleted", "close", {duration:5000});

    }

    public async updateNotifications() {
        const SERVICE = "Update notification"

        const user = this.getUser()
        if (!user) return
        await this.repoService.callSingleAPI(this.repoService.update(userUrl(user._key) + '/notifications', {}), COMPONENT, SERVICE, user)
        this.notifications.forEach(n => n.read = true)
    }

    public async createSupportTicket(ticket:SupportTicket) {
        const SERVICE = "Create support ticket"

        await this.repoService.callSingleAPI(this.repoService.create(supportTicketUrl(), ticket), COMPONENT, SERVICE, this.getUser())
        this.snackBar.open("Support ticket sent. The support team will reply by email.", "close", {duration:5000});
    }

    public async getSupportTickets() {
        const SERVICE = "Get support tickets"

        // loaded in the background on the home page, so the list shows any problem rather than a dialog
        return await this.repoService.callSingleAPI(this.repoService.getData(supportTicketUrl()), COMPONENT, SERVICE, this.getUser(), undefined, undefined, undefined, false) as SupportTicket[]
    }

}